'use client';

import { useEffect, createContext, useContext, useState, useRef, useCallback, useMemo } from 'react';
import useMediaQuery from '@fe-news/hooks/use-media-query';
import { SCREEN_SIZE } from '@fe-common-utils/constants/screen';
import { UAParser } from 'ua-parser-js';
import { type Profile } from '@fe-news/constants/DFP';

interface GPTProviderProps {
  children: React.ReactNode;
  profiles: {
    desktop: Record<string, Profile>;
    mobile: Record<string, Profile>;
  };
}

const GPTContext = createContext<Record<string, googletag.Slot | null> | null>(null);

export const useGPT = () => {
  const context = useContext(GPTContext);
  if (context === undefined) {
    throw new Error('useGPT must be used within a GPTProvider');
  }
  return context;
};

interface RefreshGptParams {
  profilesToRefresh: Record<string, Profile>;
  profilesToDestroy: Record<string, Profile>;
}

export default function GPTProvider({ children, profiles }: GPTProviderProps) {
  const isDesktop = useMemo(() => {
    if (typeof window !== 'undefined' && 'navigator' in window && 'userAgent' in window.navigator) {
      const { type = '' } = new UAParser(window.navigator.userAgent).getDevice();
      return !(type === 'mobile' || type === 'tablet');
    }
    return true;
  }, []);
  const isDesktopMediaQuery = useMediaQuery(`(min-width:${SCREEN_SIZE.DESKTOP}px)`, isDesktop);
  const [gptSlotsTable, setGptSlotsTable] = useState<Record<string, googletag.Slot | null> | null>(null);
  const mountedReference = useRef(false);

  const refreshGPT = useCallback(({ profilesToRefresh, profilesToDestroy }: RefreshGptParams) => {
    if (typeof window === 'undefined') return;

    // 確保 googletag stub 存在
    window.googletag = window.googletag || { cmd: [] };

    // 所有 GPT 操作都放進 cmd.push，確保 GPT 完全初始化後才執行
    window.googletag.cmd.push(() => {
      const profileIds = new Set([
        ...Object.values(profilesToRefresh ?? {}).map(profile => `div-gpt-ad-${profile.name}`),
        ...Object.values(profilesToDestroy ?? {}).map(profile => `div-gpt-ad-${profile.name}`)
      ]);

      // 銷毀現有的 slots
      const enabledSlots = googletag
        .pubads()
        .getSlots()
        .filter(slot => profileIds.has(slot.getSlotElementId()));
      if (enabledSlots.length > 0) {
        googletag.destroySlots(enabledSlots);
      }

      // 建立新的 slots
      const newGptSlotsTable = Object.fromEntries(
        Object.entries(profilesToRefresh ?? {}).map(([key, profile]) => {
          const slot =
            googletag
              .defineSlot(profile.path, profile.size as unknown as GptSize, `div-gpt-ad-${profile.name}`)
              ?.addService(googletag.pubads()) ?? null;
          return [key, slot];
        })
      ) as Record<string, googletag.Slot | null>;

      setGptSlotsTable(previous => {
        return previous === null ? newGptSlotsTable : { ...previous, ...newGptSlotsTable };
      });

      // 配置 GPT
      googletag.pubads().disableInitialLoad();
      googletag.pubads().enableSingleRequest();
      googletag.pubads().collapseEmptyDivs();

      // 啟用服務
      googletag.enableServices();

      // 獲取所有有效的 slots 並請求廣告
      const allSlots = Object.values(newGptSlotsTable).filter((slot): slot is googletag.Slot => slot !== undefined);

      // 請求所有廣告
      googletag.pubads().refresh(allSlots);
    });
  }, []);

  useEffect(() => {
    if (mountedReference.current) {
      const profilesToRefresh = isDesktopMediaQuery ? profiles?.desktop : profiles?.mobile;
      const profilesToDestroy = isDesktopMediaQuery ? profiles?.mobile : profiles?.desktop;
      refreshGPT({ profilesToRefresh, profilesToDestroy });
    } else {
      mountedReference.current = true;
      const profilesToRefresh = isDesktop ? profiles?.desktop : profiles?.mobile;
      const profilesToDestroy = isDesktop ? profiles?.mobile : profiles?.desktop;
      refreshGPT({ profilesToRefresh, profilesToDestroy });
    }
  }, [refreshGPT, profiles, isDesktop, isDesktopMediaQuery]);

  return <GPTContext.Provider value={gptSlotsTable}>{children}</GPTContext.Provider>;
}
